import { BookingType } from '../../redux/types/bookingTypes';
import { ExternalBooking } from '../../redux/types/integrationTypes';
import { BookingStatus } from '../../redux/types/bookingTypes';

export type BookingSource = 'internal' | 'external';
export type BookingSourceMode = BookingSource | 'all';
export type BookingListItem =
  | { source: 'internal'; key: string; booking: BookingType }
  | { source: 'external'; key: string; booking: ExternalBooking };

export type SharedBookingStatusFilters = Record<BookingStatus, boolean>;

export const filterExternalBookingsBySharedStatus = (
  bookings: ExternalBooking[],
  filters: SharedBookingStatusFilters,
  ignoreInternalOnlyStatuses = false,
): ExternalBooking[] => {
  const hasActiveFilter =
    filters.cfm ||
    filters.awt ||
    filters.cld ||
    (!ignoreInternalOnlyStatuses && (filters.new || filters.que));
  if (!hasActiveFilter) return bookings;
  return bookings.filter(
    (booking) =>
      (filters.cfm && booking.booking_status === 'cfm') ||
      (filters.awt && booking.booking_status === 'awt') ||
      (filters.cld && booking.sync_status === 'cancelled'),
  );
};

export const toBookingListItems = (
  internal: BookingType[] = [],
  external: ExternalBooking[] = [],
): BookingListItem[] =>
  [
    ...internal.map((booking) => ({
      source: 'internal' as const,
      key: `internal:${booking.id}`,
      booking,
    })),
    ...external.map((booking) => ({
      source: 'external' as const,
      key: `external:${booking.id}`,
      booking,
    })),
  ].sort((a, b) => {
    const dateDifference =
      new Date(b.booking.created_at).getTime() -
      new Date(a.booking.created_at).getTime();
    if (dateDifference) return dateDifference;
    if (a.source !== b.source) return a.source.localeCompare(b.source);
    return a.booking.id - b.booking.id;
  });

export const isBookingListSource = (
  value: string | null,
): value is BookingSource | 'all' =>
  value === 'all' || value === 'internal' || value === 'external';

export const getBookingListSource = (
  value: string | null,
  externalBookingsEnabled = true,
): BookingSourceMode => {
  if (!isBookingListSource(value)) return 'all';
  return value === 'external' && !externalBookingsEnabled ? 'all' : value;
};

export const getBookingListSources = (
  externalBookingsEnabled = true,
): BookingSourceMode[] =>
  externalBookingsEnabled
    ? ['all', 'internal', 'external']
    : ['all', 'internal'];

export const withBookingListSource = (
  params: URLSearchParams,
  source: BookingSourceMode,
): URLSearchParams => {
  const nextParams = new URLSearchParams(params);
  nextParams.set('source', source);
  return nextParams;
};
